<template>
    <div class="product-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && variant.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="variant"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />

                        <div v-if="editMode" class="attributes">
                            <attribute-badge
                                v-for="attribute in variant.attributes"
                                :key="attribute.id"
                                :attribute="attribute"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="variant"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import AttributeBadge from '@/components/attributes/Badge';
import AddEditForm from '@/components/product-variants/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Product'
    },

    validations: {
        variant: {
            sku: {
                required
            },
            price: {},
            agudaPrice: {},
            stockAmount: {
                required,
                canNotBeNegative: v => v >= 0
            },
            attributes: {
                atLeastOneItem: val => !!(val && val.length)
            }
        }
    },

    components: {
        AddEditForm,
        AttributeBadge,
        ItemInformation
    },

    data() {
        const { id = null, shopId, productId } = this.$route.params;

        return {
            id,
            editMode: !!id,
            variant: {
                sku: Math.random()
                    .toString(36)
                    .substring(2, 8)
                    .toUpperCase(),
                price: null,
                agudaPrice: null,
                attributes: []
            },
            shop: {},
            product: {},
            serverErrors: [],
            shopId,
            productId
        };
    },

    computed: {
        sections() {
            const sections = {};

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        title() {
            return `${this.editMode ? 'Edit' : 'Add'} ${
                this.product.name
            }'s Variant`;
        },

        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Shops',
                    to: '/shops'
                },
                {
                    text: this.shop.name,
                    to: `/shops/${this.shop.id}/edit`
                },
                {
                    text: `${this.shop.name}'s Products`,
                    to: `/shops/${this.shop.id}/products`
                },
                {
                    text: this.product.name,
                    to: `/shops/${this.shop.id}/products/${this.product.id}/edit`
                },
                {
                    text: `${this.editMode ? 'Edit' : 'Add'} Variant`,
                    active: true
                }
            ];
        }
    },

    async created() {
        try {
            const [shop, product] = await Promise.all([
                await this.getShop(this.shopId),
                await this.getProduct(this.productId)
            ]);

            this.shop = shop;
            this.product = product;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }

        if (this.editMode) {
            try {
                this.variant = await this.getVariant(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            getShop: 'shops/show',
            getProduct: 'products/show',
            getVariant: 'productVariants/show',
            addVariant: 'productVariants/store',
            deleteVariant: 'productVariants/destroy',
            updateVariant: 'productVariants/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const data = {
                ...this.variant,
                attributes: this.variant.attributes.map(
                    attribute => attribute.id
                )
            };

            try {
                if (!this.editMode) {
                    data.productId = this.productId;

                    const added = await this.addVariant(data);

                    this.$router.push(
                        `/shops/${this.shopId}/products/${this.productId}/variants/${added.id}/edit`
                    );
                } else {
                    const { updatedAt } = await this.updateVariant({
                        id: this.id,
                        data
                    });

                    this.variant.updatedAt = updatedAt;

                    this.$toaster('Variant updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteVariant(this.id);

                this.$router.push(
                    `/shops/${this.shop.id}/products/${this.productId}/edit`
                );
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    }
};
</script>
